import React, { useEffect, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ListChecks, Users, DollarSign, QrCode, Calendar, Briefcase, Upload, Download, Plus, Trash2, Edit2, Check, X, Mail, FileText, Award, Truck, Gift, MessageCircle, UserPlus, Copy, AlertTriangle, GraduationCap, Sparkles, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import WorkflowTab from "@/pages/pm/tabs/WorkflowTab";
import CommitteesTab from "@/pages/pm/tabs/CommitteesTab";
import AllotmentTab from "@/pages/pm/tabs/AllotmentTab";
import BudgetTab from "@/pages/pm/tabs/BudgetTab";
import KaimeraBudgetTab from "@/pages/pm/tabs/KaimeraBudgetTab";
import PrintablesTab from "@/pages/pm/tabs/PrintablesTab";
import ScannerTab from "@/pages/pm/tabs/ScannerTab";
import VendorsTab from "@/pages/pm/tabs/VendorsTab";
import ChatTab from "@/pages/pm/tabs/ChatTab";
import FormsTab from "@/pages/pm/tabs/FormsTab";
import SchoolTodosPanel from "@/components/SchoolTodosPanel";
import QueriesTab from "@/pages/pm/tabs/QueriesTab";

const TABS = [
  { key: "workflow", label: "Workflow", Icon: ListChecks },
  { key: "committees", label: "Committees", Icon: Users },
  { key: "allotment", label: "Allotment", Icon: Award },
  { key: "budget", label: "Budget", Icon: DollarSign },
  { key: "kaimera_budget", label: "Kaimera P&L", Icon: DollarSign, adminOnly: true },
  { key: "scanner", label: "QR Check-in", Icon: QrCode },
  { key: "vendors", label: "Vendors & Goods", Icon: Truck },
  { key: "forms", label: "Forms", Icon: FileText },
  { key: "printables", label: "Designs", Icon: FileText },
  { key: "school_todos", label: "School To-Dos", Icon: GraduationCap },
  { key: "queries", label: "Queries", Icon: AlertTriangle },
  { key: "chat", label: "Chat", Icon: MessageCircle },
];

export default function EventDetail() {
  const { eventId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [tab, setTab] = useState("workflow");
  const [showPrincipal, setShowPrincipal] = useState(false);
  const [principalForm, setPrincipalForm] = useState({ name: "", email: "" });
  const [principalResult, setPrincipalResult] = useState(null);
  const [registeredPrincipals, setRegisteredPrincipals] = useState([]);
  const [smManagers, setSmManagers] = useState([]);
  const [smExecutives, setSmExecutives] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [zonalPms, setZonalPms] = useState([]);
  const [showAssignSm, setShowAssignSm] = useState(false);
  const [showAssignExec, setShowAssignExec] = useState(false);
  const [showAssignDesigner, setShowAssignDesigner] = useState(false);
  const [smPick, setSmPick] = useState("");
  const [smExecPick, setSmExecPick] = useState("");
  const [designerPick, setDesignerPick] = useState("");

  const load = async () => {
    try {
      const [evR, princR, smR, execR, dsR, zpmR] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/principals`).catch(() => ({ data: [] })),
        api.get(`/users?role=social_media_manager`).catch(() => ({ data: [] })),
        api.get(`/users?role=social_media_executive`).catch(() => ({ data: [] })),
        api.get(`/users?role=designer`).catch(() => ({ data: [] })),
        api.get(`/users?role=zonal_pm`).catch(() => ({ data: [] })),
      ]);
      setEvent(evR.data);
      setRegisteredPrincipals(princR.data);
      setSmManagers(smR.data);
      setSmExecutives(execR.data);
      setDesigners(dsR.data);
      setZonalPms(zpmR.data);
      setPrincipalForm((p) => ({ ...p, email: p.email || evR.data.school_email || "" }));
      setSmPick(evR.data.sm_manager_id || "");
      setSmExecPick(evR.data.sm_executive_id || "");
    } catch { toast.error("Event not found"); }
  };
  useEffect(() => { load(); }, [eventId]);

  const reRegister = async () => {
    if (!window.confirm("Remove the current principal and register a new one?")) return;
    await api.post(`/events/${eventId}/principal/unregister`);
    toast.success("Previous principal removed");
    setShowPrincipal(true);
    setPrincipalResult(null);
    load();
  };

  const assignSm = async () => {
    await api.patch(`/events/${eventId}`, { sm_manager_id: smPick });
    toast.success("Social Media Manager assigned");
    setShowAssignSm(false);
    load();
  };

  const assignSmExec = async () => {
    try {
      await api.post(`/events/${eventId}/assign-sm-executive`, { sm_executive_id: smExecPick || null });
      toast.success(smExecPick ? "Social Media Executive assigned" : "Executive unassigned");
      setShowAssignExec(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const assignDesigner = async () => {
    if (!designerPick) return toast.error("Pick a designer");
    try {
      await api.post(`/events/${eventId}/designers`, { designer_id: designerPick });
      toast.success("Designer assigned");
      setShowAssignDesigner(false);
      setDesignerPick("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeDesigner = async (uid) => {
    if (!window.confirm("Remove this designer from the event?")) return;
    await api.delete(`/events/${eventId}/designers/${uid}`);
    load();
  };

  const registerPrincipal = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/principal-assignments", { event_id: eventId, email: principalForm.email, name: principalForm.name || "Principal" });
      setPrincipalResult(r.data);
      toast.success(r.data.email_sent ? "Principal account created & emailed" : "Principal account created");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
  };

  const copyToClipboard = (s) => { navigator.clipboard.writeText(s); toast.success("Copied"); };

  const toggleBudgetVisibility = async () => {
    const next = !(event.budget_visible_to_pm !== false); // treat undefined as true
    const action = next ? "restore" : "hide";
    if (!window.confirm(`Are you sure you want to ${action} the Budget tab for the Project Manager on this event?`)) return;
    try {
      await api.patch(`/events/${event.event_id}`, { budget_visible_to_pm: next });
      toast.success(next ? "Budget tab restored for PM" : "Budget tab hidden from PM");
      // If PM was viewing budget and it got hidden, drop them to workflow
      if (!next && tab === "budget" && user.role === "project_manager") setTab("workflow");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  if (!event) return <Layout navItems={getNav(user.role)}><div className="p-12 text-slate-400">Loading…</div></Layout>;

  const canRegisterPrincipal = ["admin", "regional_pm", "project_manager"].includes(user.role);
  const canToggleBudget = ["admin", "regional_pm"].includes(user.role);
  const budgetVisibleToPm = event.budget_visible_to_pm !== false;
  // Filter tabs: PM (and any non-admin/RPM role) doesn't see Budget when hidden
  const visibleTabs = TABS.filter((t) => {
    if (t.adminOnly && user.role !== "admin") return false;
    if (t.key !== "budget") return true;
    if (["admin", "regional_pm"].includes(user.role)) return true; // Admin/RPM always see budget
    return budgetVisibleToPm;
  });

  return (
    <Layout navItems={getNav(user.role)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <button onClick={() => nav("/dashboard")} className="text-sm text-slate-500 hover:text-orange-600 flex items-center gap-1 mb-4" data-testid="back-to-dashboard"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="dashboard-card p-6 mb-6 flex items-center gap-4">
          {event.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${event.logo_url}`} className="w-16 h-16 rounded-2xl object-cover" alt="" /> :
            <div className="w-16 h-16 rounded-2xl brand-gradient flex items-center justify-center text-white font-display font-black text-2xl">{event.name.charAt(0)}</div>}
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-black text-2xl text-slate-900">{event.name}</h1>
            <div className="text-sm text-slate-500 flex flex-wrap items-center gap-3 mt-1">
              <span>{event.school_name}</span><span>·</span>
              <span>{event.start_date} → {event.end_date}</span><span>·</span>
              <span>{event.expected_delegates} expected delegates</span>
              {event.school_email && <><span>·</span><span className="text-orange-600">{event.school_email}</span></>}
            </div>
          </div>
          {canRegisterPrincipal && (
            registeredPrincipals.length > 0 ? (
              <div className="flex items-center gap-2">
                <span className="role-pill bg-emerald-100 text-emerald-700"><Check className="w-3 h-3 inline" /> Principal registered ({registeredPrincipals[0].email})</span>
                <button onClick={reRegister} className="btn-ghost py-2 text-xs" data-testid="rereg-principal-btn"><UserPlus className="w-3 h-3" /> Re-register</button>
              </div>
            ) : (
              <button onClick={() => setShowPrincipal(true)} className="btn-primary py-2.5 text-sm whitespace-nowrap" data-testid="register-principal-btn"><UserPlus className="w-4 h-4" /> Register Principal</button>
            )
          )}
        </div>

        {canRegisterPrincipal && (
          <div className="dashboard-card p-4 mb-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-pink-500" /><span className="font-bold text-sm">Social Media Manager:</span>
              {event.sm_manager_id ? <span className="text-sm">{smManagers.find((s) => s.user_id === event.sm_manager_id)?.name || "—"}</span> : <span className="text-sm text-slate-400">Not assigned</span>}
            </div>
            <button onClick={() => setShowAssignSm(true)} className="btn-ghost text-xs py-1.5" data-testid="assign-sm-btn">Assign / Change</button>
            <div className="w-px h-6 bg-slate-200 mx-1" />
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-fuchsia-500" /><span className="font-bold text-sm">SM Executive:</span>
              {event.sm_executive_id ? <span className="text-sm">{smExecutives.find((s) => s.user_id === event.sm_executive_id)?.name || "—"}</span> : <span className="text-sm text-slate-400">Not assigned</span>}
            </div>
            <button onClick={() => setShowAssignExec(true)} className="btn-ghost text-xs py-1.5 ml-auto" data-testid="assign-sm-exec-btn">Assign / Change</button>
          </div>
        )}

        {(user.role === "admin" || user.role === "regional_pm") && (
          <div className="dashboard-card p-4 mb-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-500" /><span className="font-bold text-sm">Zonal Project Manager:</span>
              {event.zone_pm_id ? <span className="text-sm">{zonalPms.find((z) => z.user_id === event.zone_pm_id)?.name || "—"}</span> : <span className="text-sm text-slate-400">Not assigned {event.city ? `(city: ${event.city})` : ""}</span>}
            </div>
            <select
              className="input-pill text-xs py-1.5 w-56"
              value={event.zone_pm_id || ""}
              onChange={async (e) => {
                await api.patch(`/events/${eventId}/zone-pm`, { zone_pm_id: e.target.value || null });
                toast.success(e.target.value ? "Zonal PM assigned" : "Zonal PM removed");
                load();
              }}
              data-testid="assign-zone-pm-select"
            >
              <option value="">— Unassigned —</option>
              {zonalPms.map((z) => <option key={z.user_id} value={z.user_id}>{z.name} {z.zone_id ? "(zone set)" : ""}</option>)}
            </select>
          </div>
        )}

        {canRegisterPrincipal && (
          <div className="dashboard-card p-4 mb-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-fuchsia-500" /><span className="font-bold text-sm">Designers:</span>
              {(event.designer_ids || []).length === 0 ? (
                <span className="text-sm text-slate-400">None assigned</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {(event.designer_ids || []).map((did) => {
                    const d = designers.find((x) => x.user_id === did);
                    return (
                      <span key={did} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 text-xs">
                        {d?.name || did}
                        <button onClick={() => removeDesigner(did)} className="ml-1 text-rose-500 hover:text-rose-700" title="Remove">×</button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <button onClick={() => setShowAssignDesigner(true)} className="btn-ghost text-xs py-1.5 ml-auto" data-testid="assign-designer-btn">Assign designer</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar pb-3 mb-6">
          {visibleTabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${tab === t.key ? "brand-gradient text-white shadow-lg" : "bg-white text-slate-600 border border-slate-200 hover:border-orange-300"}`}>
              <t.Icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
          {canToggleBudget && (
            <button
              onClick={toggleBudgetVisibility}
              data-testid="toggle-budget-visibility-btn"
              title={budgetVisibleToPm ? "Hide Budget tab from Project Manager" : "Restore Budget tab for Project Manager"}
              className={`ml-2 flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${budgetVisibleToPm ? "bg-white text-slate-600 border-slate-200 hover:border-rose-300 hover:text-rose-600" : "bg-amber-50 text-amber-700 border-amber-200"}`}
            >
              {budgetVisibleToPm ? <><EyeOff className="w-3.5 h-3.5" /> Hide Budget from PM</> : <><Eye className="w-3.5 h-3.5" /> Budget hidden — restore</>}
            </button>
          )}
        </div>

        {tab === "workflow" && <WorkflowTab event={event} reload={load} />}
        {tab === "committees" && <CommitteesTab event={event} />}
        {tab === "allotment" && <AllotmentTab event={event} />}
        {tab === "budget" && (budgetVisibleToPm || ["admin", "regional_pm"].includes(user.role)) && <BudgetTab event={event} reload={load} />}
        {tab === "kaimera_budget" && user.role === "admin" && <KaimeraBudgetTab event={event} />}
        {tab === "printables" && <PrintablesTab event={event} />}
        {tab === "scanner" && <ScannerTab event={event} />}
        {tab === "vendors" && <VendorsTab event={event} />}
        {tab === "forms" && <FormsTab event={event} />}
        {tab === "school_todos" && <SchoolTodosPanel event={event} mode="manager" />}
        {tab === "queries" && <QueriesTab event={event} />}
        {tab === "chat" && <ChatTab event={event} />}
      </div>

      {showAssignSm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full relative">
            <ModalCloseBtn onClose={() => setShowAssignSm(false)} />
            <h3 className="font-display font-black text-2xl mb-3">Assign Social Media Manager</h3>
            <select className="input-pill" value={smPick} onChange={(e) => setSmPick(e.target.value)} data-testid="assign-sm-select">
              <option value="">— Unassigned —</option>
              {smManagers.map((s) => <option key={s.user_id} value={s.user_id}>{s.name} · {s.email}</option>)}
            </select>
            <button onClick={assignSm} className="btn-primary w-full py-3 mt-3" data-testid="assign-sm-submit">Save</button>
          </div>
        </div>
      )}

      {showAssignExec && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full relative">
            <ModalCloseBtn onClose={() => setShowAssignExec(false)} />
            <h3 className="font-display font-black text-2xl mb-1">Assign Social Media Executive</h3>
            <p className="text-xs text-slate-500 mb-3">The executive must share the PM's city. All three (PM, SM Mgr, SM Exec) can collaborate on this event.</p>
            <select className="input-pill" value={smExecPick} onChange={(e) => setSmExecPick(e.target.value)} data-testid="assign-sm-exec-select">
              <option value="">— Unassigned —</option>
              {smExecutives.map((s) => <option key={s.user_id} value={s.user_id}>{s.name} · {s.city || "—"} · {s.email}</option>)}
            </select>
            <button onClick={assignSmExec} className="btn-primary w-full py-3 mt-3" data-testid="assign-sm-exec-submit">Save</button>
          </div>
        </div>
      )}

      {showAssignDesigner && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full relative">
            <ModalCloseBtn onClose={() => setShowAssignDesigner(false)} />
            <h3 className="font-display font-black text-2xl mb-1">Assign a Designer</h3>
            <p className="text-xs text-slate-500 mb-3">Designers can be from any city. They'll be able to view this event's Social Media calendar (read-only) and attach design links per post.</p>
            <select className="input-pill" value={designerPick} onChange={(e) => setDesignerPick(e.target.value)} data-testid="assign-designer-select">
              <option value="">— Pick a designer —</option>
              {designers.filter((d) => !(event.designer_ids || []).includes(d.user_id)).map((d) => <option key={d.user_id} value={d.user_id}>{d.name} · {d.city || "any city"} · {d.email}</option>)}
            </select>
            <button onClick={assignDesigner} className="btn-primary w-full py-3 mt-3" data-testid="assign-designer-submit">Assign</button>
            {designers.length === 0 && <p className="text-xs text-amber-600 mt-2">No Designer users exist yet — Admin can create them from User Management.</p>}
          </div>
        </div>
      )}

      {showPrincipal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => { setShowPrincipal(false); setPrincipalResult(null); }}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalCloseBtn onClose={() => { setShowPrincipal(false); setPrincipalResult(null); }} />
            <h3 className="font-display font-black text-2xl mb-1">Register School Principal</h3>
            <p className="text-sm text-slate-500 mb-4">Creates a Principal account linked to this event. They'll see read-only progress stats.</p>
            {principalResult ? (
              <div>
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 mb-4">
                  <div className="text-sm text-emerald-800 font-semibold mb-2">Account ready ✅</div>
                  {principalResult.temp_password ? (
                    <>
                      <div className="text-xs text-emerald-700 mb-1">Temporary password (share securely):</div>
                      <div className="flex items-center gap-2 bg-white rounded-lg p-2 border border-emerald-200">
                        <code className="flex-1 text-sm">{principalResult.temp_password}</code>
                        <button onClick={() => copyToClipboard(principalResult.temp_password)} className="text-emerald-600"><Copy className="w-4 h-4" /></button>
                      </div>
                      <div className="text-xs text-emerald-600 mt-2">{principalResult.email_sent ? "Email also sent to the principal." : "SMTP not configured — share the password manually or set up SMTP in Admin."}</div>
                    </>
                  ) : (
                    <div className="text-xs text-emerald-700">User already existed — linked to this event.</div>
                  )}
                </div>
                <button onClick={() => { setShowPrincipal(false); setPrincipalResult(null); }} className="btn-ghost w-full py-3">Done</button>
              </div>
            ) : (
              <form onSubmit={registerPrincipal} className="space-y-3">
                <input required placeholder="Principal name" className="input-pill" value={principalForm.name} onChange={(e) => setPrincipalForm({ ...principalForm, name: e.target.value })} data-testid="principal-name-input" />
                <input required type="email" placeholder="Principal email" className="input-pill" value={principalForm.email} onChange={(e) => setPrincipalForm({ ...principalForm, email: e.target.value })} data-testid="principal-email-input" />
                <p className="text-xs text-slate-500">A temp password will be generated. SMTP will email it if configured; otherwise it appears here for you to share.</p>
                <button type="submit" className="btn-primary w-full py-3" data-testid="principal-submit"><UserPlus className="w-4 h-4" /> Create principal account</button>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </Layout>
  );
}
